import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import api from '../api/client.js';

const CATEGORIES = [
  { key: 'parts_sales',   label: 'Parts'   },
  { key: 'service_sales', label: 'Service' },
  { key: 'trailer_sales', label: 'Trailer' },
  { key: 'lease_sales',   label: 'Lease'   },
  { key: 'rental_sales',  label: 'Rental'  },
];

// Categories with invoice-level drill-down (synced from DC DMS ledger).
const DRILLABLE = { parts_sales: 'parts', trailer_sales: 'trailer' };

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtMoney(n) {
  const v = Number(n || 0);
  if (v === 0) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function rowTotal(r) {
  return CATEGORIES.reduce((s, c) => s + Number(r[c.key] || 0), 0);
}

function fmtCoverage(cov) {
  if (!cov) return '—';
  const a = `${MONTH_NAMES[cov.first.month - 1]} ${cov.first.year}`;
  const b = `${MONTH_NAMES[cov.last.month - 1]} ${cov.last.year}`;
  return `${a} → ${b} (${cov.months} mo)`;
}

export default function SalesHistory({ customerId }) {
  const [expanded, setExpanded] = useState(false);
  const [drill, setDrill] = useState(null); // { type, year, month } | null
  const q = useQuery({
    queryKey: ['sales-history', customerId],
    queryFn: () => api.get(`/sales/customers/${customerId}/history`).then(r => r.data.data),
  });

  if (q.isLoading) {
    return (
      <section className="card p-4">
        <div className="section-eyebrow">Sales history</div>
        <div className="rule-crimson" />
        <div className="text-muted text-sm mt-2">Loading…</div>
      </section>
    );
  }

  if (q.isError) {
    return (
      <section className="card p-4">
        <div className="section-eyebrow">Sales history</div>
        <div className="rule-crimson" />
        <div className="text-muted text-sm mt-2">
          {q.error?.response?.data?.error || 'Failed to load sales history.'}
        </div>
      </section>
    );
  }

  const { external_ids, seam, summary, monthly } = q.data;
  const hasAnyHistory = monthly.length > 0;
  const t12 = summary.trailing_12mo;
  const t12Total = CATEGORIES.reduce((s, c) => s + Number(t12[c.key] || 0), 0);

  // Newest months first for the table
  const ordered = [...monthly].sort((a, b) => (b.year - a.year) || (b.month - a.month));
  const visible = expanded ? ordered : ordered.slice(0, 12);

  return (
    <section className="card p-4">
      <div className="section-eyebrow">Sales history</div>
      <div className="rule-crimson" />

      {!hasAnyHistory && (
        <div className="text-muted text-sm mt-3">
          No sales history found.
          {!external_ids.karmakcal && !external_ids.excede && (
            <> This customer is not linked to KarmakCal or Excede yet.</>
          )}
        </div>
      )}

      {hasAnyHistory && (
        <>
          {/* Trailing 12-month strip */}
          <div className="mt-3">
            <div className="text-xs text-muted mb-1">Trailing 12 months</div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
              {CATEGORIES.map(c => (
                <div key={c.key} className="bg-panel rounded p-2">
                  <div className="text-[11px] text-muted uppercase tracking-wide">{c.label}</div>
                  <div className="font-semibold text-sm">{fmtMoney(t12[c.key])}</div>
                </div>
              ))}
              <div className="bg-navy text-white rounded p-2">
                <div className="text-[11px] uppercase tracking-wide opacity-80">Total</div>
                <div className="font-semibold text-sm">{fmtMoney(t12Total)}</div>
              </div>
            </div>
          </div>

          {/* Coverage line */}
          <div className="text-xs text-muted mt-3">
            <span className="mr-3">
              <span className="font-medium text-navy">KarmakCal</span>: {fmtCoverage(summary.coverage.karmakcal)}
            </span>
            <span>
              <span className="font-medium text-navy">Excede</span>: {fmtCoverage(summary.coverage.excede)}
            </span>
          </div>

          {/* Monthly table */}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b">
                  <th className="py-1 pr-2">Month</th>
                  <th className="py-1 pr-2">Source</th>
                  {CATEGORIES.map(c => (
                    <th key={c.key} className="py-1 pr-2 text-right">{c.label}</th>
                  ))}
                  <th className="py-1 pl-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(r => (
                  <tr key={`${r.year}-${r.month}-${r.source}`} className="border-b last:border-0">
                    <td className="py-1 pr-2 whitespace-nowrap">
                      {MONTH_NAMES[r.month - 1]} {r.year}
                    </td>
                    <td className="py-1 pr-2">
                      <span className={
                        'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' +
                        (r.source === 'karmakcal'
                          ? 'bg-panel text-navy'
                          : 'bg-navy text-white')
                      }>
                        {r.source === 'karmakcal' ? 'Karmak' : 'Excede'}
                      </span>
                    </td>
                    {CATEGORIES.map(c => {
                      const v = Number(r[c.key] || 0);
                      const dtype = DRILLABLE[c.key];
                      const active = drill && drill.type === dtype && drill.year === r.year && drill.month === r.month;
                      return (
                        <td key={c.key} className="py-1 pr-2 text-right tabular-nums">
                          {dtype && v > 0 ? (
                            <button
                              className={clsx('hover:underline', active ? 'text-crimson font-medium' : 'text-navy')}
                              onClick={() => setDrill(active ? null : { type: dtype, year: r.year, month: r.month })}
                              title="View invoices">
                              {fmtMoney(v)}
                            </button>
                          ) : fmtMoney(r[c.key])}
                        </td>
                      );
                    })}
                    <td className="py-1 pl-2 text-right tabular-nums font-medium">
                      {fmtMoney(rowTotal(r))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {ordered.length > 12 && (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="text-xs text-crimson mt-2 hover:underline"
            >
              {expanded ? 'Show last 12 months only' : `Show all ${ordered.length} months`}
            </button>
          )}

          {/* Invoice-level drill-down for the clicked Parts/Trailer month */}
          {drill && (
            <InvoiceDrill customerId={customerId} {...drill} onClose={() => setDrill(null)} />
          )}
          {!drill && (
            <div className="text-[11px] text-muted mt-2">Tip: click a Parts or Trailer amount to see its invoices.</div>
          )}

          <div className="text-[11px] text-muted mt-2">
            Seam: KarmakCal through {seam.karmakcal_through}, Excede from {seam.excede_from}.
          </div>
        </>
      )}
    </section>
  );
}

function InvoiceDrill({ customerId, type, year, month, onClose }) {
  const q = useQuery({
    queryKey: ['cust-invoices', customerId, type, year, month],
    queryFn: () => api
      .get(`/customers/${customerId}/invoices?type=${type}&year=${year}&month=${month}`)
      .then(r => r.data.data),
  });
  const rows = q.data || [];
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="mt-3 border border-navy-200 rounded p-3 bg-panel/40">
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">
          {type === 'parts' ? 'Parts' : 'Trailer'} invoices — {MONTH_NAMES[month - 1]} {year}
          {rows.length > 0 && <span className="text-muted normal-case"> · {rows.length} · {fmtMoney(total)}</span>}
        </div>
        <button className="text-xs text-crimson hover:underline" onClick={onClose}>close ✕</button>
      </div>
      {q.isLoading && <div className="text-muted text-sm mt-2">Loading…</div>}
      {q.isError && <div className="text-crimson text-sm mt-2">Failed to load invoices.</div>}
      {!q.isLoading && !q.isError && rows.length === 0 && (
        <div className="text-muted text-sm mt-2">
          No invoice-level detail for this month — DC DMS invoices are synced for recent months only.
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs">
            <thead className="text-left text-muted uppercase tracking-wider">
              <tr>
                <th className="py-1 pr-3">Date</th>
                <th className="py-1 pr-3">Invoice</th>
                {type === 'trailer' && <th className="py-1 pr-3">Unit</th>}
                <th className="py-1 pr-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(inv => (
                <tr key={`${inv.kind}-${inv.id}`} className="border-t border-panel">
                  <td className="py-1 pr-3 whitespace-nowrap">
                    {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-1 pr-3 font-mono text-[11px]">
                    {inv.doc_number || '—'}
                    {inv.kind === 'wo' && (
                      <span className="ml-1.5 font-sans not-italic text-[9px] uppercase tracking-wider text-gold bg-amber-100 px-1 py-0.5 rounded">on RO</span>
                    )}
                  </td>
                  {type === 'trailer' && (
                    <td className="py-1 pr-3">{inv.unit_label || inv.deal_type || '—'}</td>
                  )}
                  <td className="py-1 pr-3 text-right tabular-nums">{fmtMoney(inv.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
